import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2, AlertCircle, X } from 'lucide-react';

const PaymentSuccess = () => {
  const [countdown, setCountdown] = useState(5);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  // Get URL parameters
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      sessionId: urlParams.get('session_id'),
      planType: urlParams.get('plan')
    };
  };

  const { sessionId, planType } = getUrlParams();

  useEffect(() => {
    if (!sessionId) {
      setError('No payment session found');
      setLoading(false);
      return;
    }

    // Verify payment success
    const verifyPayment = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://mars2.mediacle.net';
        const response = await fetch(`${backendUrl}/api/stripe/verify-payment-success/${sessionId}`, {
          method: 'GET',
          credentials: 'include'
        });

        const data = await response.json();
        
        if (data.success) {
          setPaymentDetails(data);
          setShowPopup(true);
        } else {
          setError(data.message || 'Payment verification failed');
          console.error('❌ Payment verification failed:', data);
        }
      } catch (err) {
        setError('Failed to verify payment. Please contact support.');
        console.error('Payment verification error:', err);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  useEffect(() => {
    if (showPopup && paymentDetails) {
      // Start countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect to dashboard
            window.location.href = '/dashboard';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showPopup, paymentDetails]);

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'GBP',
    }).format(amount / 100);
  };

  const goToDashboardNow = () => {
    window.location.href = '/dashboard';
  };

  const closePopup = () => {
    setShowPopup(false);
    window.location.href = '/dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
            Verifying your payment...
          </h2>
          <p className="text-gray-600 dark:text-slate-400">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 mb-4">
            <AlertCircle className="h-12 w-12 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-4">Payment Verification Failed</h2>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/pricing'}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Pricing
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="w-full text-blue-600 dark:text-blue-400 px-6 py-2 border border-blue-600 dark:border-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show success popup overlay
  if (showPopup) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full p-6 relative">
          {/* Close button */}
          <button
            onClick={closePopup}
            className="absolute top-4 right-4 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Success Icon */}
          <div className="text-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-600 dark:text-slate-400">
              Thank you for your subscription. Welcome to Mars!
            </p>
          </div>

          {/* Payment Details */}
          {paymentDetails && (
            <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg mb-6 text-left">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">Subscription Details:</h3>
              <div className="space-y-1 text-sm text-gray-600 dark:text-slate-400">
                <p><span className="font-medium">Plan:</span> {paymentDetails.planType}</p>
                <p><span className="font-medium">Amount:</span> {formatAmount(paymentDetails.amountTotal, paymentDetails.currency)}</p>
                {paymentDetails.customerEmail && (
                  <p><span className="font-medium">Email:</span> {paymentDetails.customerEmail}</p>
                )}
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          <div className="mb-6">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-gray-600 dark:text-slate-400">
                Redirecting to dashboard in <span className="font-bold text-blue-600 dark:text-blue-400">{countdown}</span> seconds
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${((5 - countdown) / 5) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={goToDashboardNow}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Go to Dashboard Now
            </button>

            <button
              onClick={closePopup}
              className="w-full text-gray-600 dark:text-slate-400 py-2 px-4 border border-gray-300 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm"
            >
              Close
            </button>
          </div>

          {/* Additional Info */}
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-4 text-center">
            You can manage your subscription anytime from your account settings.
          </p>
        </div>
      </div>
    );
  }

  // Fallback - redirect to dashboard if popup is closed
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-slate-400">Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

export default PaymentSuccess;